import * as React from 'react';
import { Dispatch } from 'react-redux';
import {
	DragDropContext,
	Droppable,
	DragStart,
	DropResult,
	DroppableProvided,
	DroppableStateSnapshot,
} from 'react-beautiful-dnd';

import { Wrapper, Inner, List } from './styles';
import DraggableItem from '../../../../containers/Editor/Thumbnails/Column/DraggableItem';
import Toggle from '../../../../containers/Editor/Thumbnails/Column/Toggle';
import ButtonsPanel from '../../../../containers/Editor/Thumbnails/Column/ButtonsPanel';
import { changePageSpread, movePages } from '../../../../containers/Editor/actions';
import { StoreState } from '../../../../reducers';
import keys from '../../../../utils/keys';
import { DndItemTypes } from '../../../../typings/dnd';

interface ThumbnailsColumnProps {
	isDisplayed: boolean;
	width: number;
	currentSpreadIndex: number;
	spreads: number[][];
	isSomeEditableItemActive: boolean;
	isPagesSelectionActive: boolean;
	selectedSpreadIndices: number[];
	dispatch: Dispatch<StoreState>;
	isAccessibleVersion: boolean;
	accessiblePages: number;
}

const ThumbnailsColumn = ({
	isDisplayed,
	width,
	currentSpreadIndex,
	spreads,
	isSomeEditableItemActive,
	isPagesSelectionActive,
	selectedSpreadIndices,
	dispatch,
	isAccessibleVersion,
	accessiblePages,
}: ThumbnailsColumnProps) => {
	const [activeDraggingItem, setActiveDraggingItem] = React.useState<number | null>(null);
	const listElement = React.useRef<HTMLDivElement | null>(null);

	const handleDragStart = ({ source }: DragStart) => {
		if (isPagesSelectionActive) {
			setActiveDraggingItem(source.index);
		}
	};

	const handleDragEnd = ({ source, destination }: DropResult) => {
		if (isPagesSelectionActive) {
			setActiveDraggingItem(null);
		}

		const spreadsToMove = isPagesSelectionActive
			? selectedSpreadIndices
			: [source.index];

		if (!destination || spreadsToMove.includes(destination.index)) {
			return;
		}

		dispatch(movePages(spreadsToMove, destination.index));
	};

	React.useEffect(() => {
		if (!isDisplayed || isSomeEditableItemActive || isPagesSelectionActive) {
			return;
		}

		const handleKeyDown = (e: KeyboardEvent) => {
			if (keys.isArrowUp(e.key)) {
				e.preventDefault();
				if (currentSpreadIndex > 0) {
					dispatch(changePageSpread(currentSpreadIndex - 1));
				}
			}

			if (keys.isArrowDown(e.key)) {
				e.preventDefault();
				if (currentSpreadIndex < spreads.length - 1) {
					dispatch(changePageSpread(currentSpreadIndex + 1));
				}
			}
		};

		window.addEventListener('keydown', handleKeyDown);

		return () => {
			window.removeEventListener('keydown', handleKeyDown);
		};
	}, [currentSpreadIndex, spreads, isDisplayed, isSomeEditableItemActive, isPagesSelectionActive]);

	return (
		<Wrapper>
			<Inner
				ref={listElement}
				isDisplayed={isDisplayed}
				style={{ width: `${width}px` }}
			>
				<DragDropContext
					onDragStart={handleDragStart}
					onDragEnd={handleDragEnd}
				>
					<Droppable
						droppableId="thumbnails"
						type={DndItemTypes.EDITOR_THUMBNAIL}
						isDropDisabled={isAccessibleVersion}
					>
						{(provided: DroppableProvided, snapshot: DroppableStateSnapshot) => (
							<List
								ref={provided.innerRef}
								{...provided.droppableProps}
								isDraggingOver={snapshot.isDraggingOver}
							>
								{!isAccessibleVersion
									? spreads.map((spread, index) => (
										<DraggableItem
											key={spread[0]}
											spread={spread}
											spreadIndex={index}
											activeDraggingPosition={activeDraggingItem}
										/>
									))
									: Array.from({ length: accessiblePages }).map((_, index) => (
										<DraggableItem
											key={index}
											spread={[index]}
											spreadIndex={index}
											activeDraggingPosition={activeDraggingItem}
										/>
									))
								}
								{provided.placeholder}
							</List>
						)}
					</Droppable>
				</DragDropContext>
				{!isAccessibleVersion ? <ButtonsPanel /> : <div style={{paddingTop: '20px'}}/>}
			</Inner>
			<Toggle listElement={listElement} />
		</Wrapper>
	);
};

export default ThumbnailsColumn;



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/components/Editor/Thumbnails/Column/index.tsx